// src/utils/ageValidator.ts
// Age = competitionYear - birthYear (year-only, no exact dates).
// All mismatches are NON-BLOCKING warnings — caller decides whether to proceed.

export interface AgeValidationResult {
  valid: true;
  warning?: string;
}

/**
 * Extracts the birth year from "YYYY-MM-DD", "DD/MM/YYYY", or a native Date string.
 */
function extractBirthYear(dateOfBirth: string): number | null {
  if (!dateOfBirth) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) return parseInt(dateOfBirth.substring(0, 4), 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOfBirth)) return parseInt(dateOfBirth.substring(6, 10), 10);
  const parsed = new Date(dateOfBirth);
  return isNaN(parsed.getFullYear()) ? null : parsed.getFullYear();
}

interface AgeSpec { min: number; max: number; label: string }

const AGE_SPECS: Record<string, AgeSpec> = {
  // Internal Firestore keys (used in ranking system)
  Kids:      { min: 6,  max: 8,  label: "Kids A (age 6–8)"   },
  Fin:       { min: 9,  max: 10, label: "Kids B (age 9–10)"  },
  // UI display labels — new competitions (after rename)
  "Kids A":  { min: 6,  max: 8,  label: "Kids A (age 6–8)"   },
  "Kids B":  { min: 9,  max: 10, label: "Kids B (age 9–10)"  },
  // UI display labels — legacy competitions (before rename)
  Pin:       { min: 6,  max: 8,  label: "Kids A (age 6–8)"   },
  // Standard groups — same key in all contexts
  Cadets:    { min: 11, max: 13, label: "Cadets (age 11–13)" },
  Juniors:   { min: 14, max: 16, label: "Juniors (age 14–16)"},
  U21:       { min: 17, max: 20, label: "U21 (age 17–20)"    },
  Seniors:   { min: 17, max: 99, label: "Seniors (age 17+)"  },
  Para:      { min: 0,  max: 99, label: "Para"               },
};

/**
 * Validates age group eligibility. Always returns { valid: true }; mismatches
 * are surfaced as a `warning` string so the caller can decide whether to block.
 */
export function validateAthleteAge(
  dateOfBirth: string,
  ageGroup: string,
  competitionYear: number,
  athleteName: string
): AgeValidationResult {
  // Groups with no restriction
  if (!AGE_SPECS[ageGroup] || ageGroup === "Para") return { valid: true };

  const birthYear = extractBirthYear(dateOfBirth);
  if (birthYear === null) {
    return {
      valid: true,
      warning: `${athleteName}: no date of birth on file — please verify age group manually.`,
    };
  }

  const age = competitionYear - birthYear;
  const spec = AGE_SPECS[ageGroup];

  if (age < spec.min || age > spec.max) {
    return {
      valid: true,
      warning:
        `${athleteName} is ${age} years old in ${competitionYear} ` +
        `but ${spec.label} expects ${spec.min}–${spec.max === 99 ? spec.min + "+" : spec.max}. ` +
        `Continue anyway?`,
    };
  }

  return { valid: true };
}